import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ShoppingCart, Minus, Plus, Trash2, MessageCircle, Package, Phone } from "lucide-react";

const Cart = () => {
  const { items, updateQuantity, removeFromCart, clearCart, getTotalPrice, getTotalItems } = useCart();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleQuantityChange = (id: string, newQuantity: number) => {
    if (newQuantity < 1) {
      removeFromCart(id);
      return;
    }
    updateQuantity(id, newQuantity);
  };

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    address: '',
    notes: ''
  });
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);

  const handleCheckout = async () => {
    if (!user) {
      toast({
        title: "Inicia sesión requerido",
        description: "Debes iniciar sesión para proceder con la compra",
        variant: "destructive",
      });
      return;
    }

    if (!showCheckoutForm) {
      setShowCheckoutForm(true);
      return;
    }

    if (!customerInfo.name || !customerInfo.phone) {
      toast({
        title: "Información requerida",
        description: "Por favor completa tu nombre y teléfono",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      // Crear la orden en la base de datos
      const { data: order, error: orderError } = await supabase
        .from('orders_2025_11_10_14_11')
        .insert({
          user_id: user.id,
          total_amount: getTotalPrice(),
          customer_name: customerInfo.name,
          customer_email: user.email,
          customer_phone: customerInfo.phone,
          shipping_address: customerInfo.address,
          notes: customerInfo.notes,
          status: 'pending'
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Crear los items de la orden
      const orderItems = items.map(item => ({
        order_id: order.id,
        product_id: item.id,
        quantity: item.quantity,
        unit_price: item.price,
        total_price: item.price * item.quantity
      }));

      const { error: itemsError } = await supabase
        .from('order_items_2025_11_10_14_11')
        .insert(orderItems);

      if (itemsError) throw itemsError;

      // Generar mensaje de WhatsApp
      const whatsappMessage = generateWhatsAppMessage(order, items, customerInfo);
      
      // Abrir WhatsApp
      const whatsappUrl = `https://wa.me/573226796047?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(whatsappUrl, '_blank');

      // Marcar como enviado por WhatsApp
      await supabase
        .from('orders_2025_11_10_14_11')
        .update({ whatsapp_sent: true })
        .eq('id', order.id);

      toast({
        title: "¡Pedido creado!",
        description: `Pedido #${order.order_number} creado. Te hemos redirigido a WhatsApp para coordinar el pago.`,
      });
      
      clearCart();
      setShowCheckoutForm(false);
      setCustomerInfo({ name: '', phone: '', address: '', notes: '' });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudo procesar el pedido. Inténtalo de nuevo.",
        variant: "destructive",
      });
      console.error('Error creating order:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const generateWhatsAppMessage = (order: any, items: any[], customerInfo: any) => {
    let message = `🛒 *NUEVO PEDIDO - Amelba Soluciones*\n\n`;
    message += `📋 *Pedido:* #${order.order_number}\n`;
    message += `👤 *Cliente:* ${customerInfo.name}\n`;
    message += `📧 *Email:* ${order.customer_email}\n`;
    message += `📱 *Teléfono:* ${customerInfo.phone}\n`;
    
    if (customerInfo.address) {
      message += `📍 *Dirección:* ${customerInfo.address}\n`;
    }
    
    message += `\n🛍️ *PRODUCTOS/SERVICIOS:*\n`;
    
    items.forEach((item, index) => {
      message += `${index + 1}. ${item.name}\n`;
      message += `   • Cantidad: ${item.quantity}\n`;
      message += `   • Precio unitario: $${item.price.toFixed(2)}\n`;
      message += `   • Subtotal: $${(item.price * item.quantity).toFixed(2)}\n`;
      message += `   • Tipo: ${item.type === 'product' ? 'Producto' : 'Servicio'}\n\n`;
    });
    
    message += `💰 *TOTAL: $${getTotalPrice().toFixed(2)}*\n\n`;
    
    if (customerInfo.notes) {
      message += `📝 *Notas adicionales:*\n${customerInfo.notes}\n\n`;
    }
    
    message += `⏰ *Fecha:* ${new Date().toLocaleString('es-CO')}\n\n`;
    message += `Por favor, confirma este pedido y proporciona la información de pago. ¡Gracias! 🙏`;
    
    return message;
  };

  if (items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <ShoppingCart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Tu carrito está vacío</h1>
          <p className="text-muted-foreground mb-8">
            Explora nuestros productos y servicios para agregar items a tu carrito.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/productos">
              <Button size="lg">
                <Package className="mr-2 h-5 w-5" />
                Ver Productos
              </Button>
            </Link>
            <Link to="/servicios">
              <Button size="lg" variant="outline">
                Ver Servicios
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold">Carrito de Compras</h1>
            <Badge variant="secondary" className="text-lg px-3 py-1">
              {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'}
            </Badge>
          </div>

          <div className="space-y-4">
            {items.map((item) => (
              <Card key={item.id}>
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Item Image */}
                    <div className="w-full sm:w-24 h-24 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      {item.image_url ? (
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Item Details */}
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-lg">{item.name}</h3>
                          <Badge variant="outline" className="w-fit mt-1">
                            {item.type === 'product' ? 'Producto' : 'Servicio'}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {/* Quantity Controls */}
                          {item.type === 'product' && (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="w-12 text-center font-medium">{item.quantity}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          )}

                          {/* Price */}
                          <div className="text-right">
                            <div className="font-bold text-lg">
                              ${(item.price * item.quantity).toFixed(2)}
                            </div>
                            {item.quantity > 1 && (
                              <div className="text-sm text-muted-foreground">
                                ${item.price.toFixed(2)} c/u
                              </div>
                            )}
                          </div>

                          {/* Remove Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFromCart(item.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Clear Cart */}
          <div className="mt-6">
            <Button 
              variant="outline" 
              onClick={clearCart}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Vaciar Carrito
            </Button>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle>Resumen del Pedido</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>${getTotalPrice().toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Envío:</span>
                <span className="text-green-600">Gratis</span>
              </div>
              <Separator />
              <div className="flex justify-between font-bold text-lg">
                <span>Total:</span>
                <span className="text-primary">${getTotalPrice().toFixed(2)}</span>
              </div>

              {!user && (
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">
                    Inicia sesión para proceder con la compra
                  </p>
                  <Link to="/login">
                    <Button variant="outline" size="sm" className="w-full">
                      Iniciar Sesión
                    </Button>
                  </Link>
                </div>
              )}

              {/* Checkout Form */}
              {showCheckoutForm && user && (
                <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                  <h3 className="font-semibold">Información de Contacto</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre Completo *</Label>
                    <Input
                      id="name"
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Tu nombre completo"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Teléfono *</Label>
                    <Input
                      id="phone"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Ej: +57 300 123 4567"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address">Dirección de Envío (Opcional)</Label>
                    <Input
                      id="address"
                      value={customerInfo.address}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Dirección completa para envío"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notas Adicionales (Opcional)</Label>
                    <Textarea
                      id="notes"
                      value={customerInfo.notes}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Instrucciones especiales, preferencias de entrega, etc."
                      rows={3}
                    />
                  </div>
                </div>
              )}

              <Button 
                className="w-full" 
                size="lg"
                onClick={handleCheckout}
                disabled={!user || isProcessing}
              >
                {!showCheckoutForm ? (
                  <>
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Proceder al Pago
                  </>
                ) : (
                  <>
                    <Phone className="mr-2 h-5 w-5" />
                    {isProcessing ? 'Enviando a WhatsApp...' : 'Enviar Pedido por WhatsApp'}
                  </>
                )}
              </Button>

              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  📱 Te contactaremos por WhatsApp para coordinar el pago
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Métodos: Transferencia, Nequi, Daviplata, Efectivo
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Cart;